/**
 * Wem gehört ein Kanal einer Visitenkarte — der Firma (Zentrale) oder der
 * Person?
 *
 * Eine Visitenkarte nennt meist beides: die Zentrale der Firma (info@, „-0",
 * „Tel. Zentrale") und die Durchwahl, das Handy und die persönliche E-Mail der
 * Person. Bis 09/2026 kannte der Scan nur EIN Telefon und EINE E-Mail — bei
 * einer Firmenkarte landeten Durchwahl, Handy und persönliche Adresse der
 * Person an der Firma (Plan 2026-09-30-ocr-scan-kontaktanlage, V3–V6).
 *
 * Die Regeln stehen hier, in forms-core, weil sie an zwei Stellen gelten: im
 * Backend nach dem OCR-Lauf (`visitenkarte-nachbearbeitung.ts`) und im Browser
 * für die vCard, die gar nicht über den Server läuft. Zwei Regelwerke drifteten
 * auseinander, und dann ordnete dieselbe Karte je nach Weg anders zu.
 *
 * Die Regeln sind hart, nicht bloß Vorschläge an das Modell: Was das Modell als
 * `bezug` meldet, gilt nur, wo keine der Regeln unten greift.
 *
 *   V3  info@, kontakt@, office@ … und Nummern auf „-0" bzw. mit der
 *       Beschriftung „Zentrale" gehören zur Firma.
 *   V4  Steht nur eine Durchwahl da, wird die Zentrale daraus abgeleitet
 *       (`zentraleAusNummern`) — als Vorschlag, markiert mit `abgeleitet`.
 *   V5  Ein Handy gehört nie zur Firma.
 *   V6  Eine persönliche Adresse (vorname.nachname@, v.nachname@, der Name der
 *       Person im lokalen Teil) gehört nie zur Firma.
 */
import { zentraleAusNummern } from "./telefon.js";
/**
 * Lokale Teile, die eine Sammeladresse der Firma kennzeichnen (V3). Englische
 * Gegenstücke stehen mit drin, weil Karten international gedruckt werden.
 */
export const ZENTRALE_EMAIL_PRAEFIXE = [
    "info",
    "kontakt",
    "contact",
    "office",
    "zentrale",
    "service",
    "mail",
    "hallo",
    "hello",
];
/** Kleinbuchstaben, Umlaute gefaltet — wie sie in einer Mailadresse stehen. */
const falten = (s) => s
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z]/g, "");
/** Die Namensteile der Person, gefaltet; kurze Teile (≤ 2) zählen nicht. */
const namensteile = (person) => [person?.vorname, person?.nachname]
    .flatMap((n) => String(n ?? "").split(/[\s-]+/))
    .map(falten)
    .filter((t) => t.length > 2);
/** Ist die Adresse eine Sammeladresse der Firma (info@, kontakt@ …)? */
export function istZentraleEmail(email) {
    const lokal = email.trim().toLowerCase().split("@")[0] ?? "";
    // „info.de@", „office-koeln@": der Präfix vor einem Trenner zählt auch.
    const kopf = lokal.split(/[._-]/)[0];
    return ZENTRALE_EMAIL_PRAEFIXE.includes(lokal) || ZENTRALE_EMAIL_PRAEFIXE.includes(kopf);
}
/**
 * Ist die Adresse erkennbar die einer Person (V6)?
 *
 * Mit bekanntem Namen: ein Namensteil steht im lokalen Teil
 * („w.kranz@", „wolfgang.kranz@", „kranz@"). Ohne Namen: das Muster
 * vorname.nachname@ bzw. v.nachname@.
 */
export function istPersoenlicheEmail(email, person) {
    const lokal = email.trim().toLowerCase().split("@")[0] ?? "";
    if (!lokal || istZentraleEmail(email))
        return false;
    const gefaltet = falten(lokal);
    if (namensteile(person).some((t) => gefaltet.includes(t)))
        return true;
    return /^[a-z]+[._-][a-z]{2,}$/.test(lokal);
}
/** Nur die Ziffern (mit führendem „+"), „(0)" nach der Ländervorwahl entfernt. */
const kompakt = (nummer) => nummer.replace(/\(0\)/g, "").replace(/[^\d+]/g, "");
/** Deutsche Mobilfunknummer (015x, 016x, 017x) — V5. */
export function istMobilnummer(nummer) {
    const k = kompakt(nummer);
    const national = k.startsWith("+49") ? k.slice(3) : k.startsWith("0049") ? k.slice(4) : k.startsWith("0") ? k.slice(1) : null;
    return national !== null && /^1[5-7]\d{6,}/.test(national);
}
/** Nummer auf „-0" (Zentrale, V3). */
export function endetAufNull(nummer) {
    return /[-‐-―−]\s*0\s*$/.test(nummer.trim());
}
/** Beschriftung „Zentrale", „Tel. Zentrale", „Central", „Switchboard". */
export function istZentraleBeschriftung(beschriftung) {
    return /zentral|central|switchboard/i.test(String(beschriftung ?? ""));
}
const TYPEN = new Set(["telefon", "mobil", "fax", "email"]);
/** Doppelte gleichen Typs zusammenfassen — Ziffern bzw. Adresse zählen. */
const schluessel = (k) => k.typ === "email" ? `email|${k.wert.trim().toLowerCase()}` : `nr|${kompakt(k.wert)}`;
/**
 * Ordnet die Kanäle einer Karte Firma bzw. Person zu (V3–V6) und leitet bei
 * Bedarf die Zentrale ab (V4).
 *
 * @param person Name der Person auf der Karte. Nennt die Karte keine Person,
 *   gehört alles, was kein Handy ist, der Firma.
 */
export function kanaeleZuordnen(roh, person) {
    const mitPerson = [person?.vorname, person?.nachname].some((n) => String(n ?? "").trim() !== "");
    const out = [];
    const gesehen = new Set();
    for (const r of roh) {
        const wert = String(r?.wert ?? "").trim();
        if (!wert)
            continue;
        let typ = String(r?.typ ?? "").toLowerCase();
        if (!TYPEN.has(typ))
            continue;
        // Ein Handy bleibt ein Handy, auch wenn es als „Tel." beschriftet ist.
        if (typ === "telefon" && istMobilnummer(wert))
            typ = "mobil";
        const vorschlag = r.bezug === "zentral" || r.bezug === "person" ? r.bezug : undefined;
        let bezug;
        if (typ === "mobil") {
            bezug = "person"; // V5
        }
        else if (typ === "email") {
            if (istZentraleEmail(wert))
                bezug = "zentral"; // V3
            else if (istPersoenlicheEmail(wert, person))
                bezug = "person"; // V6
            else
                bezug = vorschlag ?? (mitPerson ? "person" : "zentral");
        }
        else if (typ === "fax") {
            // Ein Fax ist praktisch immer das Gerät der Firma; die Personen-Maske
            // hat dafür auch kein eigenes Feld.
            bezug = vorschlag === "person" && !endetAufNull(wert) ? "person" : "zentral";
        }
        else if (endetAufNull(wert) || istZentraleBeschriftung(r.beschriftung)) {
            bezug = "zentral"; // V3
        }
        else {
            bezug = vorschlag ?? (mitPerson ? "person" : "zentral");
        }
        const key = schluessel({ typ, wert });
        if (gesehen.has(key))
            continue;
        gesehen.add(key);
        out.push({ typ, wert, bezug });
    }
    // V4: Keine Zentrale, aber eine Durchwahl der Person → Zentrale ableiten.
    const hatZentrale = out.some((k) => k.typ === "telefon" && k.bezug === "zentral");
    if (!hatZentrale) {
        const durchwahlen = out.filter((k) => k.typ === "telefon" && k.bezug === "person").map((k) => k.wert);
        const [basis] = zentraleAusNummern(durchwahlen);
        if (basis)
            out.push({ typ: "telefon", wert: `${basis}-0`, bezug: "zentral", abgeleitet: true });
    }
    return out;
}
